import * as React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';

import { i18n } from 'app/i18n';
import { BillingAccount } from 'app/page/orders/model/billing-account';
import { RootState } from 'app/redux/state/root-state';
import { RootActions } from 'app/redux/action/root-actions';
import { setSelectedOrderIdActionCreator } from 'app/page/orders/redux/orders-action-creators';
import { AddressMapper } from 'app/page/orders/mapper/address-mapper';
import houseIcon from 'app/resources/house-with-tree.svg';
import { BillingAccountUtils } from 'app/page/orders/utils/billing-account-utils';
import { ActiveOrder } from 'app/page/orders/model/active-order';
import { AddressSelectButton } from 'app/components/domain/select-button';
import { NavigationService, navigationService } from 'app/service/navigation-service';
import { ChoosePage } from 'app/components/domain/choose-page';

interface StateProps {
    billingAccounts: BillingAccount[];
    selectedBillingAccountId?: string;
}

interface DispatchProps {
    setSelectedOrderId: typeof setSelectedOrderIdActionCreator;
}

type Props = StateProps & DispatchProps;

class ChooseServiceLocationPageComponent extends React.Component<Props> {
    private readonly handleSelectOrder = (activeOrder: ActiveOrder): void => {
        const { billingAccounts, selectedBillingAccountId, setSelectedOrderId } = this.props;

        if (!selectedBillingAccountId) {
            throw new Error('Billing account must be selected.');
        }

        const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );

        const {
            order: { id },
        } = activeOrder;

        if (billingAccount.selectedOrderId !== id) {
            setSelectedOrderId(id!, selectedBillingAccountId);
        } else {
            navigationService.goToPath(NavigationService.SUMMARY);
        }
    };

    public renderOrders(billingAccount: BillingAccount): React.ReactNode {
        return Object.values(billingAccount.activeOrders).map((activeOrder) => {
            const {
                order: { id, deliveryServiceAddress },
            } = activeOrder;

            return (
                <AddressSelectButton
                    key={id}
                    title={AddressMapper.toDisplayStreet(deliveryServiceAddress!)}
                    text={deliveryServiceAddress?.addressParameters?.dwellingUnit}
                    icon={{
                        src: houseIcon,
                        alt: i18n.image.houseWithTree,
                    }}
                    onClick={() => this.handleSelectOrder(activeOrder)}
                />
            );
        });
    }

    public render(): React.ReactNode {
        const { billingAccounts, selectedBillingAccountId } = this.props;

        if (!selectedBillingAccountId) {
            return null;
        }

        const billingAccount: BillingAccount = BillingAccountUtils.getBillingAccount(
            billingAccounts,
            selectedBillingAccountId,
        );
        const { initialized } = billingAccount;

        if (!initialized) {
            return null;
        }

        return (
            initialized && (
                <ChoosePage
                    title={i18n.chooseServiceLocation.title}
                    subtitle={i18n.chooseServiceLocation.whichAddress}
                    content={this.renderOrders(billingAccount)}
                />
            )
        );
    }
}

const mapStateToProps = (state: RootState): StateProps => {
    const {
        orders: { billingAccounts, selectedBillingAccountId },
    } = state;

    return {
        billingAccounts,
        selectedBillingAccountId,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootActions>): DispatchProps => ({
    ...bindActionCreators(
        {
            setSelectedOrderId: setSelectedOrderIdActionCreator,
        },
        dispatch,
    ),
});

const ChooseServiceLocationPage = connect(mapStateToProps, mapDispatchToProps)(ChooseServiceLocationPageComponent);

export { ChooseServiceLocationPage };
